/**
 * Testing recommendations engine.
 *
 * Rule-based: each rule inspects real patient + fertility-intent data and, when
 * it matches, adds the referenced test IDs with a short clinical rationale.
 * Every rule cites a specific society guideline so the rationale can be audited
 * against the source of truth. Nothing is invented from feel.
 *
 * Each recommendation carries three copy fields with different lengths:
 *   - `triggerKey`  — chip-sized "Because you …" label (≤ ~40 chars)
 *   - `reasonKey`   — one-line rationale used on the recommendation card
 *   - `detailKey`   — longer rationale retained for future recommendation work
 *
 * Sources referenced (all are society guidelines / committee opinions, not
 * primary studies — every rule can be audited against the referenced document).
 * Verified against society websites in July 2026:
 *
 * - ASRM 2020: Testing and interpreting measures of ovarian reserve — Committee Opinion.
 *   Still current (last searched 2026-07).
 *   https://www.asrm.org/practice-guidance/practice-committee-documents/testing-and-interpreting-measures-of-ovarian-reserve-a-committee-opinion-2020/
 * - ASRM 2021: Fertility evaluation of infertile women — Committee Opinion. Fertil Steril 2021.
 *   https://www.asrm.org/practice-guidance/practice-committee-documents/fertility-evaluation-of-infertile-women-a-committee-opinion-2021/
 * - ASRM 2026 RPL: Recurrent pregnancy loss — Committee Opinion (June 2026, first update
 *   since 2012). Fertil Steril 2026. Notable additions: genetic evaluation of miscarriage
 *   tissue should be offered to all patients; uterine anatomy evaluation should be offered
 *   to all women with RPL.
 *   https://www.asrm.org/practice-guidance/practice-committee-documents/recurrent-pregnancy-loss-a-committee-opinion-2026/
 * - ASRM 2024 PGT-A: The use of preimplantation genetic testing for aneuploidy — Committee
 *   Opinion (2024, supersedes 2018). Fertil Steril 2024. Notable: routine PGT-A has not
 *   demonstrated benefit; explicitly NOT recommended for advanced paternal age.
 *   https://www.asrm.org/practice-guidance/practice-committee-documents/the-use-of-preimplantation-genetic-testing-for-aneuploidy-a-committee-opinion-2024/
 * - AUA/ASRM 2024: Diagnosis and Treatment of Infertility in Men (2020 guideline, amended
 *   2024). Notable: sperm DNA fragmentation is NOT recommended in the initial evaluation;
 *   IS recommended for couples with recurrent pregnancy loss.
 *   https://www.auanet.org/documents/Guidelines/PDF/2024%20Guidelines/Male%20Infertility%20Unabridged%20Final.pdf
 * - WHO 2021: WHO Laboratory Manual for the Examination and Processing of Human Semen, 6th ed.
 *   https://www.who.int/publications/i/item/9789240030787
 * - ESHRE 2023 PCOS: 2023 International Evidence-based Guideline for the Assessment and
 *   Management of Polycystic Ovary Syndrome. Hum Reprod 2023;38(9):1655-1679.
 *   https://www.eshre.eu/Guidelines-and-Legal/Guidelines/Polycystic-Ovary-Syndrome
 * - ESHRE 2022 Endometriosis: Endometriosis Guideline (2022).
 *   https://www.eshre.eu/Guidelines-and-Legal/Guidelines/Endometriosis-guideline
 * - ESHRE 2022 RPL: Recurrent Pregnancy Loss — Guideline (update in 2022, published 2023).
 *   Bender Atik R, Christiansen OB, Elson J, et al. Hum Reprod Open 2023;2023(1):hoad002.
 *   https://academic.oup.com/hropen/article/2023/1/hoad002/7068067
 * - ESHRE 2023 RIF: Good practice recommendations on recurrent implantation failure.
 *   Cimadomo D, de Los Santos MJ, Griesinger G, et al. Hum Reprod Open 2023;2023(3):hoad023.
 *   https://academic.oup.com/hropen/article/2023/3/hoad023/7241238
 * - ACOG 2017 (reaffirmed 2023): Committee Opinion 691 — Carrier Screening for Genetic Conditions.
 *   https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2017/03/carrier-screening-for-genetic-conditions
 * - ACMG 2021: Screening for autosomal recessive and X-linked conditions during pregnancy
 *   and preconception — a practice resource. Gregg AR, Aarabi M, Klugman S, et al.
 *   Genet Med 2021;23(10):1793-1806. (Complemented by ACMG 2024 lab technical standard.)
 *   https://www.gimjournal.org/article/S1098-3600(21)01138-9/fulltext
 * - ATA 2026: American Thyroid Association 2026 Guidelines for Thyroid Disease in
 *   Preconception, Pregnancy, and Postpartum. Thyroid 2026. Supersedes ATA 2017.
 *   https://www.thyroid.org/new-ata-guidelines-for-thyroid-disease-in-preconception-pregnancy-and-postpartum/
 * - Agarwal et al. 2020: Sperm DNA fragmentation — a new guideline for clinicians.
 *   World J Mens Health 2020;38(4):412-471. Referenced alongside AUA/ASRM 2024 for the
 *   varicocele-specific fragmentation indication.
 *   https://pubmed.ncbi.nlm.nih.gov/32777875/
 */

import { loadContentData } from "@src/packages/content/loader";
import { getBlocksByKind } from "@src/packages/content/registry";
import type { ContentBlockId } from "@src/packages/content/types";
import type { Patient } from "@src/packages/medical-history/basic-info/calculateBasicInfoCompletion";
import type { FertilityIntent } from "@src/packages/fertility-intent/fertilityIntent.types";
import type { Cycle } from "@src/packages/cycles/hooks/useCycles";
import type { CanonicalExperienceContext } from "@src/packages/experience/canonicalExperienceContext";

loadContentData(["testing"]);

type RecommendationSource =
  | "ASRM_2020_OVARIAN_RESERVE"
  | "ASRM_2021_INFERTILITY_WOMAN"
  | "ASRM_2026_RPL"
  | "ASRM_2024_PGT"
  | "AUA_ASRM_2024_MALE"
  | "WHO_2021_SEMEN"
  | "ESHRE_2023_PCOS"
  | "ESHRE_2022_ENDO"
  | "ESHRE_2022_RPL"
  | "ESHRE_2023_RIF"
  | "ACOG_2017_CARRIER"
  | "ACMG_2021_ECS"
  | "ATA_2026_THYROID"
  | "AGARWAL_2020_DNA_FRAG";

export type Recommendation = {
  testId: string;
  triggerKey: string;
  reasonKey: string;
  detailKey: string;
  source: RecommendationSource;
  priority: number;
};

type Ctx = {
  gender: "AFAB" | "AMAB" | null;
  age: number | null;
  diseases: string[];
  hadVaricoceles: boolean;
  reportedConditions: string[];
  goal: string | null;
  plannedIvf: boolean;
  plannedIui: boolean;
  irregularPeriods: boolean;
  hasCancerHistory: boolean;
  /** ≥2 miscarriages on the ObGyn history — ESHRE / ASRM RPL definition. */
  hasRecurrentPregnancyLoss: boolean;
  /**
   * ≥3 closed IVF/ICSI/FET cycles that ended without a pregnancy — proxy for
   * recurrent implantation failure (ESHRE 2023 working definition).
   */
  hasRecurrentImplantationFailure: boolean;
  /** Ethnicity string as reported by the patient (free-text). */
  ethnicity: string | null;
};

function computeAge(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/**
 * Count closed treatment cycles that ended without a clinical pregnancy — used
 * as a proxy for recurrent implantation failure per ESHRE 2023.
 * Only IVF-family cycles (ivf, icsi, fet) count; preservation cycles are
 * excluded because their "closure" isn't a failure signal.
 */
function countFailedTreatmentCycles(cycles: Cycle[] | undefined): number {
  if (!cycles?.length) return 0;
  const TREATMENT_TYPES = new Set(["ivf", "icsi", "fet"]);
  return cycles.filter((c) => {
    if (c.status !== "closed") return false;
    if (!TREATMENT_TYPES.has(c.cycleType)) return false;
    const outcomeType = (c as { outcome?: { type?: string | null } }).outcome
      ?.type;
    // "no_pregnancy", "miscarriage", "cancelled" all count as a failed transfer
    // attempt per ESHRE working definition.
    return (
      outcomeType === "no_pregnancy" ||
      outcomeType === "miscarriage" ||
      outcomeType === "cancelled"
    );
  }).length;
}

function countMiscarriages(patient: Patient | null | undefined): number {
  const pregnancies = patient?.obGynHistory?.pregnancies ?? [];
  return pregnancies.filter((p) => p.outcome === "miscarriage").length;
}

function buildCtx(
  patient: Patient | null | undefined,
  intent: FertilityIntent | null | undefined,
  cycles: Cycle[] | undefined,
  canonical: CanonicalExperienceContext | null | undefined,
): Ctx {
  const gender =
    canonical === null
      ? null
      : canonical
        ? canonical.viewer.sexAtBirth === "AFAB" ||
          canonical.viewer.sexAtBirth === "AMAB"
          ? canonical.viewer.sexAtBirth
          : null
        : patient?.gender === "AFAB" || patient?.gender === "AMAB"
          ? patient.gender
          : null;
  const diseases = (patient?.medicalHistory?.diseases ?? [])
    .map((d) => (d.disease ?? "").toLowerCase())
    .filter(Boolean);
  const hadVaricoceles =
    patient?.medicalHistory?.hadVaricoceles === "yes_diagnosed" ||
    patient?.medicalHistory?.hadVaricoceles === "yes_undiagnosed";
  const reportedConditions = (intent?.reportedConditions ?? []).map((c) =>
    String(c).toLowerCase(),
  );
  const plannedModalities = canonical
    ? canonical.intent.plannedModalities
    : (intent?.plannedModalities ?? []);
  const acceptedTreatmentFamily =
    canonical?.acceptedTreatmentPlan?.treatmentFamily;
  const acceptedFertilizationMethod =
    canonical?.acceptedTreatmentPlan?.fertilizationMethod;
  return {
    gender,
    age: computeAge(patient?.dateOfBirth),
    diseases,
    hadVaricoceles,
    reportedConditions,
    goal: canonical ? canonical.intent.goal : (intent?.goal ?? null),
    plannedIvf:
      acceptedTreatmentFamily === "ivf" ||
      acceptedFertilizationMethod === "icsi" ||
      plannedModalities.includes("ivf"),
    plannedIui:
      acceptedTreatmentFamily === "iui" || plannedModalities.includes("iui"),
    irregularPeriods:
      intent?.periodRegularity === "irregular" ||
      intent?.periodRegularity === "no_periods",
    hasCancerHistory: patient?.medicalHistory?.hasCancerHistory === true,
    // ESHRE 2022 & ASRM 2020 define RPL as ≥2 pregnancy losses.
    hasRecurrentPregnancyLoss: countMiscarriages(patient) >= 2,
    // ESHRE 2023 working RIF definition: no clinical pregnancy after ≥3 embryo
    // transfers (proxied here as ≥3 closed treatment cycles without pregnancy).
    hasRecurrentImplantationFailure: countFailedTreatmentCycles(cycles) >= 3,
    ethnicity: patient?.ethnicity ?? null,
  };
}

function hasCondition(ctx: Ctx, condition: string): boolean {
  return (
    ctx.diseases.includes(condition) ||
    ctx.reportedConditions.includes(condition)
  );
}

type Rule = {
  match: (ctx: Ctx) => boolean;
  recs: Omit<Recommendation, "priority">[];
  priority: number;
};

const K = "access.testing.recommendation";

const TEST_BLOCKS_BY_ID = new Map(
  getBlocksByKind("test").map((test) => [String(test.id), test]),
);

export function isEligibleForPersonalizedRecommendation(
  testId: string,
): boolean {
  const test = TEST_BLOCKS_BY_ID.get(testId);
  return Boolean(test && test.body.evidenceStrength !== "debunked");
}

export function filterEligiblePersonalizedRecommendations(
  recommendations: Recommendation[],
): Recommendation[] {
  return recommendations.filter((recommendation) =>
    isEligibleForPersonalizedRecommendation(recommendation.testId),
  );
}

/**
 * Rules are ordered by clinical priority (lower = higher). Duplicate testIds
 * from later rules are dropped so the first matching rationale wins.
 */
const RULES: Rule[] = [
  // Highest urgency: cancer history triggers fertility preservation baseline
  // ahead of everything else. Applies to both AFAB and AMAB.
  {
    priority: 8,
    match: (c) => c.hasCancerHistory && c.gender === "AFAB",
    recs: [
      {
        testId: "test-amh",
        triggerKey: `${K}.trigger.cancer_history`,
        reasonKey: `${K}.reason.amh_cancer`,
        detailKey: `${K}.detail.amh_cancer`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
      {
        testId: "test-afc",
        triggerKey: `${K}.trigger.cancer_history`,
        reasonKey: `${K}.reason.afc_cancer`,
        detailKey: `${K}.detail.afc_cancer`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
      {
        testId: "test-general-bloodwork",
        triggerKey: `${K}.trigger.cancer_history`,
        reasonKey: `${K}.reason.hormone_cancer`,
        detailKey: `${K}.detail.hormone_cancer`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
      {
        testId: "test-carrier-screening",
        triggerKey: `${K}.trigger.cancer_history`,
        reasonKey: `${K}.reason.carrier_cancer`,
        detailKey: `${K}.detail.carrier_cancer`,
        source: "ACOG_2017_CARRIER",
      },
    ],
  },
  // Priority 9: Recurrent pregnancy loss (≥2 miscarriages).
  // ESHRE 2022 recommends: thyroid function + TPO-Ab, antiphospholipid /
  // thrombophilia screen, uterine cavity assessment.
  // ASRM 2026 (first update in over a decade) adds: genetic evaluation of
  // miscarriage tissue offered to all patients + uterine anatomy evaluation
  // offered to all women. Parental karyotype now conditional on prior
  // findings rather than routine (both societies aligned on this in 2026).
  // AUA/ASRM 2024 male-infertility amendment: sperm DNA fragmentation IS
  // specifically recommended for couples with RPL.
  {
    priority: 9,
    match: (c) => c.hasRecurrentPregnancyLoss,
    recs: [
      {
        testId: "test-thrombophilia",
        triggerKey: `${K}.trigger.rpl`,
        reasonKey: `${K}.reason.thrombophilia_rpl`,
        detailKey: `${K}.detail.thrombophilia_rpl`,
        source: "ESHRE_2022_RPL",
      },
      {
        testId: "test-thyroid-panel",
        triggerKey: `${K}.trigger.rpl`,
        reasonKey: `${K}.reason.thyroid_rpl`,
        detailKey: `${K}.detail.thyroid_rpl`,
        source: "ESHRE_2022_RPL",
      },
      {
        testId: "test-saline-sonogram",
        triggerKey: `${K}.trigger.rpl`,
        reasonKey: `${K}.reason.sonogram_rpl`,
        detailKey: `${K}.detail.sonogram_rpl`,
        source: "ASRM_2026_RPL",
      },
      {
        testId: "test-karyotype",
        triggerKey: `${K}.trigger.rpl`,
        reasonKey: `${K}.reason.karyotype_rpl`,
        detailKey: `${K}.detail.karyotype_rpl`,
        source: "ASRM_2026_RPL",
      },
      {
        testId: "test-semen-analysis",
        triggerKey: `${K}.trigger.rpl`,
        reasonKey: `${K}.reason.dna_frag_rpl`,
        detailKey: `${K}.detail.dna_frag_rpl`,
        source: "AUA_ASRM_2024_MALE",
      },
    ],
  },
  // Priority 9: Recurrent implantation failure (≥3 failed IVF/ICSI/FET) —
  // ESHRE 2023 good-practice recommendations. Endometrial cavity + receptivity
  // + chronic endometritis + sperm DNA fragmentation + PGT-A discussion.
  {
    priority: 9,
    match: (c) => c.hasRecurrentImplantationFailure,
    recs: [
      {
        testId: "test-chronic-endometritis-testing",
        triggerKey: `${K}.trigger.rif`,
        reasonKey: `${K}.reason.endometritis_rif`,
        detailKey: `${K}.detail.endometritis_rif`,
        source: "ESHRE_2023_RIF",
      },
      {
        testId: "test-endometrial-receptivity-testing",
        triggerKey: `${K}.trigger.rif`,
        reasonKey: `${K}.reason.era_rif`,
        detailKey: `${K}.detail.era_rif`,
        source: "ESHRE_2023_RIF",
      },
      {
        testId: "test-semen-analysis",
        triggerKey: `${K}.trigger.rif`,
        reasonKey: `${K}.reason.dna_frag_rif`,
        detailKey: `${K}.detail.dna_frag_rif`,
        source: "ESHRE_2023_RIF",
      },
      {
        testId: "test-saline-sonogram",
        triggerKey: `${K}.trigger.rif`,
        reasonKey: `${K}.reason.sonogram_rif`,
        detailKey: `${K}.detail.sonogram_rif`,
        source: "ESHRE_2023_RIF",
      },
      {
        testId: "test-pgt-a",
        triggerKey: `${K}.trigger.rif`,
        reasonKey: `${K}.reason.pgt_a_rif`,
        detailKey: `${K}.detail.pgt_a_rif`,
        source: "ESHRE_2023_RIF",
      },
    ],
  },
  {
    priority: 8,
    match: (c) => c.hasCancerHistory && c.gender === "AMAB",
    recs: [
      {
        testId: "test-semen-analysis",
        triggerKey: `${K}.trigger.cancer_history`,
        reasonKey: `${K}.reason.semen_cancer`,
        detailKey: `${K}.detail.semen_cancer`,
        source: "WHO_2021_SEMEN",
      },
      {
        testId: "test-semen-analysis",
        triggerKey: `${K}.trigger.cancer_history`,
        reasonKey: `${K}.reason.dna_frag_cancer`,
        detailKey: `${K}.detail.dna_frag_cancer`,
        source: "AUA_ASRM_2024_MALE",
      },
      {
        testId: "test-carrier-screening",
        triggerKey: `${K}.trigger.cancer_history`,
        reasonKey: `${K}.reason.carrier_cancer`,
        detailKey: `${K}.detail.carrier_cancer`,
        source: "ACOG_2017_CARRIER",
      },
    ],
  },
  {
    priority: 10,
    match: (c) => c.gender === "AFAB",
    recs: [
      {
        testId: "test-amh",
        triggerKey: `${K}.trigger.baseline_afab`,
        reasonKey: `${K}.reason.amh_baseline`,
        detailKey: `${K}.detail.amh_baseline`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
      {
        testId: "test-afc",
        triggerKey: `${K}.trigger.baseline_afab`,
        reasonKey: `${K}.reason.afc_baseline`,
        detailKey: `${K}.detail.afc_baseline`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
    ],
  },
  // AMAB baseline: semen analysis is the foundation of male fertility
  // evaluation (WHO 2021 lab manual defines reference values; AUA/ASRM 2024
  // guideline endorses it as the first-line test in every male workup).
  {
    priority: 12,
    match: (c) => c.gender === "AMAB",
    recs: [
      {
        testId: "test-semen-analysis",
        triggerKey: `${K}.trigger.baseline_amab`,
        reasonKey: `${K}.reason.semen_baseline`,
        detailKey: `${K}.detail.semen_baseline`,
        source: "AUA_ASRM_2024_MALE",
      },
    ],
  },
  // AUA/ASRM 2024 explicitly does NOT recommend sperm DNA fragmentation in the
  // initial evaluation of the infertile couple. It IS recommended in the
  // varicocele setting (Agarwal 2020 clinical guideline, still current) and
  // for couples with recurrent pregnancy loss (AUA/ASRM 2024, endorsed).
  // Age alone is no longer a supported indication per AUA/ASRM 2024.
  {
    priority: 15,
    match: (c) => c.gender === "AMAB" && c.hadVaricoceles,
    recs: [
      {
        testId: "test-semen-analysis",
        triggerKey: `${K}.trigger.varicocele`,
        reasonKey: `${K}.reason.dna_frag_varicocele`,
        detailKey: `${K}.detail.dna_frag_varicocele`,
        source: "AGARWAL_2020_DNA_FRAG",
      },
    ],
  },
  {
    priority: 20,
    match: (c) => c.gender === "AFAB" && hasCondition(c, "pcos"),
    recs: [
      {
        testId: "test-general-bloodwork",
        triggerKey: `${K}.trigger.pcos`,
        reasonKey: `${K}.reason.hormone_pcos`,
        detailKey: `${K}.detail.hormone_pcos`,
        source: "ESHRE_2023_PCOS",
      },
      {
        testId: "test-fertility-tracker",
        triggerKey: `${K}.trigger.pcos`,
        reasonKey: `${K}.reason.tracker_pcos`,
        detailKey: `${K}.detail.tracker_pcos`,
        source: "ESHRE_2023_PCOS",
      },
    ],
  },
  {
    priority: 21,
    match: (c) => c.gender === "AFAB" && hasCondition(c, "early_menopause"),
    recs: [
      {
        testId: "test-general-bloodwork",
        triggerKey: `${K}.trigger.early_menopause_reported`,
        reasonKey: `${K}.reason.hormone_early_menopause`,
        detailKey: `${K}.detail.hormone_early_menopause`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
      {
        testId: "test-fsh",
        triggerKey: `${K}.trigger.early_menopause_reported`,
        reasonKey: `${K}.reason.fsh_early_menopause`,
        detailKey: `${K}.detail.fsh_early_menopause`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
    ],
  },
  {
    priority: 22,
    match: (c) => c.gender === "AFAB" && hasCondition(c, "endometriosis"),
    recs: [
      {
        testId: "test-saline-sonogram",
        triggerKey: `${K}.trigger.endo`,
        reasonKey: `${K}.reason.sonogram_endo`,
        detailKey: `${K}.detail.sonogram_endo`,
        source: "ESHRE_2022_ENDO",
      },
      {
        testId: "test-chronic-endometritis-testing",
        triggerKey: `${K}.trigger.endo`,
        reasonKey: `${K}.reason.endometritis_endo`,
        detailKey: `${K}.detail.endometritis_endo`,
        source: "ESHRE_2022_ENDO",
      },
    ],
  },
  {
    priority: 23,
    match: (c) => c.gender === "AFAB" && hasCondition(c, "fibroids"),
    recs: [
      {
        testId: "test-saline-sonogram",
        triggerKey: `${K}.trigger.fibroids_reported`,
        reasonKey: `${K}.reason.sonogram_fibroids`,
        detailKey: `${K}.detail.sonogram_fibroids`,
        source: "ASRM_2021_INFERTILITY_WOMAN",
      },
      {
        testId: "test-baseline-ultrasound",
        triggerKey: `${K}.trigger.fibroids_reported`,
        reasonKey: `${K}.reason.baseline_ultrasound_fibroids`,
        detailKey: `${K}.detail.baseline_ultrasound_fibroids`,
        source: "ASRM_2021_INFERTILITY_WOMAN",
      },
    ],
  },
  {
    priority: 24,
    match: (c) => c.gender === "AFAB" && hasCondition(c, "low_ovarian_reserve"),
    recs: [
      {
        testId: "test-general-bloodwork",
        triggerKey: `${K}.trigger.low_reserve`,
        reasonKey: `${K}.reason.hormone_low_reserve`,
        detailKey: `${K}.detail.hormone_low_reserve`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
    ],
  },
  {
    priority: 25,
    match: (c) => c.gender === "AFAB" && hasCondition(c, "ovarian_surgery"),
    recs: [
      {
        testId: "test-general-bloodwork",
        triggerKey: `${K}.trigger.ovarian_surgery_reported`,
        reasonKey: `${K}.reason.hormone_ovarian_surgery`,
        detailKey: `${K}.detail.hormone_ovarian_surgery`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
    ],
  },
  {
    priority: 25,
    match: (c) => hasCondition(c, "thyroid_disease"),
    recs: [
      {
        testId: "test-thyroid-panel",
        triggerKey: `${K}.trigger.thyroid_reported`,
        reasonKey: `${K}.reason.thyroid_reported`,
        detailKey: `${K}.detail.thyroid_reported`,
        source: "ATA_2026_THYROID",
      },
    ],
  },
  {
    priority: 27,
    match: (c) => c.gender === "AFAB" && hasCondition(c, "tubal_issues"),
    recs: [
      {
        testId: "test-saline-sonogram",
        triggerKey: `${K}.trigger.tubal_issues_reported`,
        reasonKey: `${K}.reason.sonogram_tubal`,
        detailKey: `${K}.detail.sonogram_tubal`,
        source: "ASRM_2021_INFERTILITY_WOMAN",
      },
    ],
  },
  {
    priority: 26,
    match: (c) => c.gender === "AFAB" && (c.age ?? 0) >= 35,
    recs: [
      {
        testId: "test-thyroid-panel",
        triggerKey: `${K}.trigger.age_35_plus`,
        reasonKey: `${K}.reason.thyroid_age`,
        detailKey: `${K}.detail.thyroid_age`,
        source: "ATA_2026_THYROID",
      },
    ],
  },
  {
    priority: 28,
    match: (c) => c.gender === "AFAB" && c.irregularPeriods,
    recs: [
      {
        testId: "test-prolactin",
        triggerKey: `${K}.trigger.irregular_cycles`,
        reasonKey: `${K}.reason.prolactin_irregular`,
        detailKey: `${K}.detail.prolactin_irregular`,
        source: "ASRM_2021_INFERTILITY_WOMAN",
      },
      {
        testId: "test-thyroid-panel",
        triggerKey: `${K}.trigger.irregular_cycles`,
        reasonKey: `${K}.reason.thyroid_irregular`,
        detailKey: `${K}.detail.thyroid_irregular`,
        source: "ASRM_2021_INFERTILITY_WOMAN",
      },
    ],
  },
  // Priority 29: Ethnicity provided — ACMG 2021 + ACOG 690 (2017, reaffirmed
  // 2023) both recommend expanded (pan-ethnic) carrier screening for anyone
  // considering conception, and specifically note higher-frequency conditions
  // in some backgrounds. We only fire this when the patient has told us their
  // ethnicity — otherwise we fall through to the generic planned-cycle rule.
  {
    priority: 29,
    match: (c) =>
      !!c.ethnicity &&
      c.ethnicity.trim().length > 0 &&
      c.ethnicity.trim().toLowerCase() !== "prefer_not_to_say",
    recs: [
      {
        testId: "test-carrier-screening",
        triggerKey: `${K}.trigger.ethnicity`,
        reasonKey: `${K}.reason.carrier_ethnicity`,
        detailKey: `${K}.detail.carrier_ethnicity`,
        source: "ACMG_2021_ECS",
      },
      {
        testId: "test-karyotype",
        triggerKey: `${K}.trigger.ethnicity`,
        reasonKey: `${K}.reason.karyotype_ethnicity`,
        detailKey: `${K}.detail.karyotype_ethnicity`,
        source: "ACOG_2017_CARRIER",
      },
    ],
  },
  {
    priority: 30,
    match: (c) => c.plannedIvf || c.plannedIui,
    recs: [
      {
        testId: "test-carrier-screening",
        triggerKey: `${K}.trigger.planned_cycle`,
        reasonKey: `${K}.reason.carrier_planned_cycle`,
        detailKey: `${K}.detail.carrier_planned_cycle`,
        source: "ACOG_2017_CARRIER",
      },
      {
        testId: "test-infectious-disease",
        triggerKey: `${K}.trigger.planned_cycle`,
        reasonKey: `${K}.reason.infectious_planned_cycle`,
        detailKey: `${K}.detail.infectious_planned_cycle`,
        source: "ASRM_2021_INFERTILITY_WOMAN",
      },
    ],
  },
  {
    priority: 32,
    match: (c) => c.plannedIvf && c.gender === "AFAB",
    recs: [
      {
        testId: "test-lining-check",
        triggerKey: `${K}.trigger.planned_ivf`,
        reasonKey: `${K}.reason.lining_ivf`,
        detailKey: `${K}.detail.lining_ivf`,
        source: "ASRM_2021_INFERTILITY_WOMAN",
      },
    ],
  },
  {
    // ASRM 2018 PGT-A committee opinion: aneuploidy risk rises sharply from
    // ~35% at age 35 to ~50% at 40 and >75% at 43. PGT-A discussion is
    // warranted from 38 onward when IVF is planned.
    priority: 33,
    match: (c) => c.gender === "AFAB" && (c.age ?? 0) >= 38 && c.plannedIvf,
    recs: [
      {
        testId: "test-pgt-a",
        triggerKey: `${K}.trigger.age_38_plus_ivf`,
        reasonKey: `${K}.reason.pgt_a_age`,
        detailKey: `${K}.detail.pgt_a_age`,
        source: "ASRM_2024_PGT",
      },
    ],
  },
  {
    priority: 34,
    match: (c) => c.goal === "egg-freezing" || c.goal === "egg-preserve",
    recs: [
      {
        testId: "test-general-bloodwork",
        triggerKey: `${K}.trigger.egg_freezing`,
        reasonKey: `${K}.reason.hormone_egg_freezing`,
        detailKey: `${K}.detail.hormone_egg_freezing`,
        source: "ASRM_2020_OVARIAN_RESERVE",
      },
    ],
  },
];

/**
 * Compute ranked recommendations for a patient. The top N results are what the
 * hero should surface. `cycles` is optional — passing it enables the recurrent
 * implantation failure rule (ESHRE 2023).
 */
export function getTestingRecommendations(
  patient: Patient | null | undefined,
  intent: FertilityIntent | null | undefined,
  cycles?: Cycle[],
  limit = 5,
  canonical?: CanonicalExperienceContext | null,
): Recommendation[] {
  const ctx = buildCtx(patient, intent, cycles, canonical);
  if (!ctx.gender) return [];

  const seen = new Map<string, Recommendation>();
  for (const rule of RULES) {
    if (!rule.match(ctx)) continue;
    for (const rec of rule.recs) {
      if (seen.has(rec.testId)) continue;
      seen.set(rec.testId, { ...rec, priority: rule.priority });
    }
  }

  return filterEligiblePersonalizedRecommendations(Array.from(seen.values()))
    .sort((a, b) => a.priority - b.priority)
    .slice(0, limit);
}

export type { ContentBlockId };
